import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Paper,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import dayjs from "dayjs";

export default function AuditDetailsView({
  audit,
  onClose,
  onEdit,
  onExpand,
  onRevise,
  isFullScreen = false,
}) {
  const [expanded, setExpanded] = useState(false);

  const Field = ({ label, value }) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" fontWeight="bold">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
        {value || "Not provided"}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        flex: 1,
        p: 2,
        border: isFullScreen ? "none" : "1px solid #ccc",
        borderRadius: isFullScreen ? 0 : 2,
        backgroundColor: "#FFFFFF",
        position: "relative",
        marginLeft: isFullScreen ? 0 : 2,
        height: isFullScreen ? "100%" : "auto",
        width: "100%",
        maxWidth: isFullScreen ? "100%" : "98%",
        overflow: "auto",
        // Remove any border-left or border-right styling that could cause blue borders
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          left: 0,
          width: "100%",
          backgroundColor: "white",
          color: "black", // Changed from potentially blue color
          p: 1,
          borderTopLeftRadius: isFullScreen ? 0 : 2,
          borderTopRightRadius: isFullScreen ? 0 : 2,
          display: "flex",
          justifyContent: "flex-end",
          borderBottom: "1px solid #eee",
          zIndex: 1000,
        }}
      >
        {!isFullScreen && (
          <Button
            startIcon={<FullscreenIcon sx={{ color: "#4F46E5" }} />}
            sx={{
              textTransform: "none",
              borderRadius: "4px",
              mr: 1,
              fontSize: "14px",
              border: "1px solid #e0e0e0",
              color: "#4F46E5",
            }}
            onClick={onExpand}
          >
            Expand
          </Button>
        )}
        <Button
          startIcon={<EditIcon sx={{ color: "#4F46E5" }} />}
          sx={{
            textTransform: "none",
            borderRadius: "4px",
            mr: 1,
            fontSize: "14px",
            border: "1px solid #e0e0e0",
            color: "#4F46E5",
          }}
          onClick={onEdit}
        >
          Edit
        </Button>
        <IconButton
          sx={{
            color: "#4F46E5",
            p: 0.5,
          }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ mt: 6, pt: 2, px: isFullScreen ? 16 : 4, width: "100%" }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: "#f9f9f9",
            maxWidth: "100%",
            width: "100%",
          }}
        >
          <Typography variant="h5" sx={{ mb: 3, color: "#4F46E5" }}>
            {audit.title || "No Title Available"}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Field label="Record Stage" value={audit.recordStage} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Field label="Audit Status" value={audit.auditStatus} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Field
                label="Date"
                value={
                  audit.date
                    ? dayjs(audit.date).format("MMMM D, YYYY")
                    : "Not specified"
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Field
                label="Quality Owner"
                value={audit.qualityOwner.zc_display_value}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Field label="Quality Origin" value={audit.qualityOrigin} />
            </Grid>
          </Grid>
        </Paper>

        {/* Description information */}
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: "#f5f5f5",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="h6">Description Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ p: 2 }}>
              <Field label="Description" value={audit.description} />
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Clause 4.3: Scope */}
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: "#f5f5f5",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="h6">Clause 4.3: Scope</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Field
                  label="1. Is the scope statement in the QMS still accurate?"
                  value={audit.scopeAccurate}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="2. Are all applicable activities covered by the QMS scope?"
                  value={audit.activitiesCovered}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Clause 4.4: Process review */}
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: "#f5f5f5",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="h6">Clause 4.4: Process review</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Field
                  label="1. Is the QMS accurate re: the top-level processes are identified?"
                  value={audit.qmsAccurate}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="2. Are inputs and outputs defined in some manner for each top-level process?"
                  value={audit.inputsOutputs}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="3. Are process resources (people, facilities and equipment) provided as necessary?"
                  value={audit.processResources}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="4. Does each top-level process have people assigned responsibility and authority?"
                  value={audit.peopleAssigned}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="5. If a process doesn't meet a goal or measurement, is corrective action taken?"
                  value={audit.correctiveAction}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="6. Does management seek to improve the processes over time?"
                  value={audit.processImprovement}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Clause 5.3: Roles and responsibilities */}
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: "#f5f5f5",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="h6">
              Clause 5.3: Roles and responsibilities
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={12}>
                <Field
                  label="1. Are company roles and responsibilities defined and communicated through the company?"
                  value={audit.rolesResponsibilities}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Clause 8.1: Process review */}
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: "#f5f5f5",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="h6">Clause 8.1: Process review</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Field
                  label="1. Are the processes planned with an eye towards the customer's product requirements?"
                  value={audit.processesPlanned}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="2. Are outsourced processes controlled?"
                  value={audit.outsourcedProcesses}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="3. Are the inspection/test criteria for product defined?"
                  value={audit.inspectionCriteria}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="4. Are procedures developed to support manufacturing processes (where needed)?"
                  value={audit.supportProcedures}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Clause 8.2.1: Quoting */}
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: "#f5f5f5",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="h6">Clause 8.2.1: Quoting</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Field
                  label="1. Is the procedure for quoting up to date and being followed?"
                  value={audit.quotingProcedure}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="2. Are customer questions related to jobs, products, etc. routed and processed in an appropriate way?"
                  value={audit.customerQuestions}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Clause 8.2.2: Customer requirements */}
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: "#f5f5f5",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="h6">
              Clause 8.2.2: Customer requirements
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Field
                  label="1. Are customer requirements for product captured accurately?"
                  value={audit.customerRequirementsCaptured}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="2. Are these requirements reviewed before the company commits to delivering the products?"
                  value={audit.requirementsReviewed}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Clause 8.2.3: Customer requirements */}
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: "#f5f5f5",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="h6">
              Clause 8.2.3: Customer requirements
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Field
                  label="1. Does this review include the applicable requirements?"
                  value={audit.applicableRequirements}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="2. If a quote was prepared previously, is this reviewed against the incoming order and any deviations resolved with the customer?"
                  value={audit.quoteReviewed}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Continue with other clauses */}
        {/* Clause 8.2.4: Order entry */}
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: "#f5f5f5",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="h6">Clause 8.2.4: Order entry</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Field
                  label="1. If either the customer or the company changes the order requirements after it's been taken, is this change agreed to with the customer?"
                  value={audit.changeAgreed}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="2. Are changes to all necessary orders documented?"
                  value={audit.changesDocumented}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Clause 8.3: Customer Design */}
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: "#f5f5f5",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="h6">Clause 8.3: Customer Design</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ width: "100%", p: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Field
                  label="1. Is the procedure for accepting customer design being followed?"
                  value={audit.acceptingProcedure}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="2. Are there records of the accepting customer design?"
                  value={audit.designRecords}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="3. Do activities include necessary review of customer provided designs?"
                  value={audit.designReview}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="4. Are there records kept if a customer changes design?"
                  value={audit.designChanges}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="5. Are there records kept on approval by customers of the design?"
                  value={audit.designApproval}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="6. Are there records of any actions taken to prevent nonconformities?"
                  value={audit.nonconformityPrevention}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Clause 8.4: Purchasing */}
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: "#f5f5f5",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="h6">Clause 8.4: Purchasing</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Field
                  label="1. Are suppliers subject to controls to ensure the quality of purchased products?"
                  value={audit.supplierControls}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="2. Are suppliers evaluated against some criteria to ensure they meet company expectations for quality?"
                  value={audit.supplierEvaluations}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="3. Are the records of supplier evaluations up to date and accurate?"
                  value={audit.evaluationRecords}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="4. Are items inspected at receiving?"
                  value={audit.receivingInspection}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="5. Are PO's reviewed before release to the supplier?"
                  value={audit.poReviews}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="6. Do the outgoing PO's include all necessary information about the items or services being purchased (description, quantity, due date, etc.)?"
                  value={audit.poInformation}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Clause 8.5: Production */}
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: "#f5f5f5",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="h6">Clause 8.5: Production</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Field
                  label="1. Are production operators provided with information describing the product to be made?"
                  value={audit.productInfo}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="2. Are suitable inspection devices provided which can be used to inspect or test products?"
                  value={audit.inspectionDevices}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="3. Are in-process inspections or tests conducted as required?"
                  value={audit.inProcessInspections}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="4. Are the facilities and equipment adequate to ensure the quality of the work?"
                  value={audit.facilitiesEquipment}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="5. Are operators trained as required?"
                  value={audit.operatorTraining}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="6. Is product identified at all stages as to what it is and/or what order it belongs to?"
                  value={audit.productIdentification}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="7. Does product identification include a means to identify its inspection status?"
                  value={audit.inspectionStatus}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="8. If the company uses customer or supplier provided hardware, is this identified as the property owner?"
                  value={audit.propertyOwner}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="9. Are shelf-life items only used if their expiration dates have not expired?"
                  value={audit.shelfLifeItems}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="10. Is the product properly handled during all operations, to prevent damage?"
                  value={audit.productHandling}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="11. Is the product properly stored while awaiting use or shipment, to prevent damage?"
                  value={audit.productStorage}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="12. If the company does post-delivery work, does this address related customer requirements?"
                  value={audit.postDeliveryWork}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="13. Does post-delivery work address customer complaints or feedback?"
                  value={audit.customerComplaints}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="14. Are changes to manufacturing processes only implemented through a formal method that ensures the changes are reviewed and authorized?"
                  value={audit.processChanges}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Clause 8.6: Final inspection */}
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: "#f5f5f5",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="h6">Clause 8.6: Final inspection</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Field
                  label="1. Are final inspections or tests conducted to ensure the product meets requirements before delivery?"
                  value={audit.finalinspectionsORtestsConducted}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="2. Are final inspection records kept?"
                  value={audit.finalinspectionrecordskept}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="3. Do the final inspection records include the name or identification of the person who performed the inspection?"
                  value={audit.finalinspectionrecordsinclude}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Clause 8.7: Non-conforming product */}
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: "#f5f5f5",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="h6">
              Clause 8.7: Non-conforming product
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Field
                  label="1. Are nonconforming products properly identified and controlled in some manner to ensure they are not accidentally used or shipped?"
                  value={audit.nonconformingproductsproperlyidentified}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="2. Does the company notify customers if a nonconforming product is discovered after it has been delivered?"
                  value={audit.doesthecompanynotifycustomers}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="3. Is nonconforming product dealt with by one or more of the following ways: corrected, contained, segregated, reworked, repaired or accepted as is?"
                  value={audit.nonconformingproductdealtwith}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="4. Is nonconforming product segregated or contained, to ensure it does not cross-contaminate conforming products?"
                  value={audit.nonconformingproductsegregatedor}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="5. Is the customer notified when applicable?"
                  value={audit.customernotifiedwhenapplicable}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="6. If a product is reworked or repaired, is it then re-inspected?"
                  value={audit.productisreworkedorrepaired}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="7. Are records of the nonconformities maintained?"
                  value={audit.recordsofthenonconformities}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="8. Do these records include the actions taken, customer approvals or waivers received, and the person(s) authorizing the final disposition?"
                  value={audit.customerapprovalsorwaivers}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Clause 9.1: Business performance */}
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: "#f5f5f5",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="h6">
              Clause 9.1: Business performance
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Field
                  label="1. Are methods underway to monitor and measure customer satisfaction?"
                  value={audit.methodsunderwaytomonitor}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="2. Is the company measuring product quality data?"
                  value={audit.companymeasuringproduct}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="3. Is the company measuring process KPIs or objectives?"
                  value={audit.companykpi}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="4. Are suppliers being measured in terms of performance?"
                  value={audit.suppliersbeingmeasured}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );
}
